import React, { useRef, useEffect, useState } from "react";
import styles from "./CommunicationPage.module.css";
import { useHistory } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";

import Header from "../components/Header";
import Loader from "components/Loader";
import Checkbox from "components/Checkbox";

import { useAccount } from "utils/AccountInfoContext";
import { makeAuthedRequest } from "utils/makeAuthedRequest";
import { useTranslation } from "react-i18next";

import Modal from "components/Modal";
import Button from "components/Button";

export function CommunicationPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { activeUser, usersData, isLoading } = useAccount();

  const emailDebounceRef = useRef(null);
  const smsDebounceRef = useRef(null);

  const [showErrorModal, setShowErrorModal] = useState(null);

  const safeUsersData = Array.isArray(usersData) ? usersData : [];

  const user = safeUsersData.find((u) => u.userId === activeUser?.userId);

  // EMAIL
  const emailMutation = useMutation(
    async (allowEmails) => {
      const response = await makeAuthedRequest("v1/settings/communications", {
        method: "POST",
        body: JSON.stringify({
          contactPK: activeUser?.contactId,
          allowEmails,
        }),
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error("Failed to update email preference");
      }

      return response;
    },
    {
      onMutate: async (newValue) => {
        await queryClient.cancelQueries(["usersData"]);

        const previous = queryClient.getQueryData(["usersData"]);

        queryClient.setQueryData(["usersData"], (old) => {
          if (!Array.isArray(old)) return old;

          return old.map((u) =>
            u.userId === activeUser?.userId ? { ...u, allowSMS: newValue } : u
          );
        });

        return { previous };
      },
      onError: (err, newValue, context) => {
        if (context?.previous) {
          queryClient.setQueryData(["usersData"], context.previous);
        }

        setErrorModal("Failed to update email preference");
      },
      onSettled: () => {
        queryClient.invalidateQueries(["usersData"]);
      },
    }
  );

  // SMS
  const smsMutation = useMutation(
    async (allowSMS) => {
      const response = await makeAuthedRequest("v1/settings/communications", {
        method: "POST",
        body: JSON.stringify({
          contactPK: activeUser?.contactId,
          allowSMS,
        }),
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error("Failed to update SMS preference");
      }

      return response;
    },
    {
      onMutate: async (newValue) => {
        await queryClient.cancelQueries(["usersData"]);

        const previous = queryClient.getQueryData(["usersData"]);

        queryClient.setQueryData(["usersData"], (old) => {
          if (!Array.isArray(old)) return old;

          return old.map((u) =>
            u.userId === activeUser?.userId ? { ...u, allowSMS: newValue } : u
          );
        });

        return { previous };
      },
      onError: (err, newValue, context) => {
        if (context?.previous) {
          queryClient.setQueryData(["usersData"], context.previous);
        }

        setErrorModal("Failed to update text preference");
      },
      onSettled: () => {
        queryClient.invalidateQueries(["usersData"]);
      },
    }
  );

  // navigation
  const handleHeaderBackClick = () => {
    history.push("/");
  };

  // debounced handlers
  const handleEmailChange = (event) => {
    const newValue = event.target.checked;

    if (emailDebounceRef.current) {
      clearTimeout(emailDebounceRef.current);
    }

    emailDebounceRef.current = setTimeout(() => {
      emailMutation.mutate(newValue);
    }, 250);
  };

  const handleSmsChange = (event) => {
    const newValue = event.target.checked;

    if (smsDebounceRef.current) {
      clearTimeout(smsDebounceRef.current);
    }

    smsDebounceRef.current = setTimeout(() => {
      smsMutation.mutate(newValue);
    }, 250);
  };

  const setErrorModal = (message) => {
    setShowErrorModal(message);
  };

  // cleanup
  useEffect(() => {
    return () => {
      if (emailDebounceRef.current) clearTimeout(emailDebounceRef.current);
      if (smsDebounceRef.current) clearTimeout(smsDebounceRef.current);
    };
  }, []);

  // render
  if (isLoading) {
    return (
      <div className={styles.container}>
        <Header header={t("Settings")} onClick={handleHeaderBackClick} />
        <Loader color="#415364" size={85} />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Header header={t("Settings")} onClick={handleHeaderBackClick} />

      <div className={styles.header}>{t("Communication from WWG")}</div>

      {/* EMAIL */}
      <div className={styles.item}>
        <Checkbox
          isChecked={user?.allowEmails ?? false}
          onChange={handleEmailChange}
          disabled={emailMutation.isLoading}
        />

        <div className={styles.label}>{t("Receive Emails from WWG")}</div>
      </div>

      {/* SMS */}
      <div className={styles.item}>
        <Checkbox
          isChecked={user?.allowSMS ?? false}
          onChange={handleSmsChange}
          disabled={smsMutation.isLoading}
        />

        <div className={styles.label}>
          {t("Receive Text Messages from WWG")}
        </div>
      </div>
      {showErrorModal && (
        <Modal isOpen={showErrorModal}>
          <div className={styles.errorModal}>
            <p className={styles.text}>{showErrorModal}</p>
          </div>
          <div className={styles.button}>
            <Button
              buttonText={t("Okay")}
              onClick={() => {
                setErrorModal(null);
              }}
            />
          </div>
        </Modal>
      )}
    </div>
  );
}

export default CommunicationPage;
